import { Button, Col, Row, Typography } from "antd";
import { formatMoney } from "../../../Utils/Functions";
import { useSancorGuarantee } from "./hook";
import "./styles.less";

/**
 * Logo blanco, para la banda magenta. Va en el CDN como los de Mapfre, Itaú y Sudameris, y
 * con el mismo `<img>` plano que ellos: `ImagenOptimizada` sirve el pixel transparente hasta
 * que `react-measure` mide, perdiendo de paso el `alt`.
 *
 * Es PNG y no SVG a propósito: el SVG subido al CDN sale con `content-type: text/plain`
 * (medido el 2026-08-26) y un `<img>` no lo renderiza. El archivo es de 324x104, el doble
 * exacto del slot de la banda.
 */
const SANCOR_LOGO = "https://cdn2.infocasas.com.uy/web/6a8f30fb084c6_logo-sancor-seguros.png";
const LOGO_WIDTH = 139;
const LOGO_HEIGHT = 45;

/** En la tira del sidebar el logo entra más chico (nodo 2046:8074 del Figma). */
const CARD_LOGO_WIDTH = 92;
const CARD_LOGO_HEIGHT = 30;

const GANCHO = "Conocé el costo de tu garantía";

/** Alto del Affix del header en desktop, el mismo descuento que usa MapfreFinancing. */
const AFFIX_HEIGHT = 30;

/**
 * Los `maxLength` son los mismos que valida `SancorGuaranteeLeadRequest` en IC2 — no un
 * criterio propio. Están acá para que un campo largo no llegue a rebotar como 400: ese
 * cuerpo trae el error por campo en vez de un texto (ver `hook.tsx`).
 */
const CAMPOS = [
	{
		name: "first_name",
		label: "Nombre",
		type: "text",
		autoComplete: "given-name",
		placeholder: "Ej. María",
		maxLength: 100,
		span: 12,
	},
	{
		name: "last_name",
		label: "Apellido",
		type: "text",
		autoComplete: "family-name",
		placeholder: "Ej. Godoy",
		maxLength: 100,
		span: 12,
	},
	{
		name: "document",
		label: "C.I.",
		type: "text",
		autoComplete: "off",
		placeholder: "Cédula de identidad",
		maxLength: 30,
		span: 12,
	},
	{
		name: "phone",
		label: "Celular",
		type: "tel",
		autoComplete: "tel",
		placeholder: "09X XXX XXX",
		maxLength: 30,
		span: 12,
	},
	{
		name: "email",
		label: "Mail",
		type: "email",
		autoComplete: "email",
		placeholder: "tu@mail.com",
		maxLength: 150,
		span: 24,
	},
];

/**
 * El importe va en dos lugares distintos según el ancho: dentro de la banda magenta en
 * desktop, y debajo de ella sobre fondo claro en mobile. Se renderiza en los dos y el CSS
 * esconde el que no corresponde — elegirlo en JS con `useBreakpoint` haría que el SSR emita
 * el layout equivocado hasta que hidrate.
 */
const PolicyQuote = ({ currency, premium }) => (
	<div className="sancor-quote">
		<Typography className="sancor-quote-label">Costo anual de póliza</Typography>
		<Typography className="sancor-quote-amount">
			{currency} {formatMoney(premium)}
		</Typography>
	</div>
);

export const SancorGuarantee = ({
	id,
	price,
	isDisabledProperty = false,
	showFinancingCard = false,
	financingRef = null,
}) => {
	const { quote, form, handleChange, handleSubmit, status, message } = useSancorGuarantee({
		propertyId: id,
		price,
	});

	// El aviso no admite garantía: no hay un número honesto que mostrar, así que el widget
	// no existe. Las razones y sus volúmenes medidos están en `hook.tsx`. Va antes de la
	// tira a propósito: si el aviso no es elegible, tampoco se ofrece calcular.
	if (quote === null) return null;

	// La tira del sidebar (sólo desktop: su Col es `span={0} lg={7}`). El ref apunta al Col
	// que envuelve el Financing de la columna principal, donde está el widget entero.
	if (showFinancingCard) {
		const onScrollToGuarantee = () => {
			if (!financingRef?.current) return;

			window.scrollTo({
				top: financingRef.current.offsetTop - AFFIX_HEIGHT,
				behavior: "smooth",
			});
		};

		return (
			<div className="property-sancor-guarantee-card">
				<div className="sancor-card-container">
					<img
						className="sancor-card-logo"
						src={SANCOR_LOGO}
						alt="Sancor Seguros"
						width={CARD_LOGO_WIDTH}
						height={CARD_LOGO_HEIGHT}
					/>
					<Typography className="sancor-card-tagline">{GANCHO}</Typography>
					<Button className="sancor-card-btn" onClick={onScrollToGuarantee}>
						Calcular
					</Button>
				</div>
			</div>
		);
	}

	const policyQuote = <PolicyQuote currency={price?.currency?.name} premium={quote.premium} />;

	return (
		<div className="property-sancor-guarantee">
			<div className="sancor-band">
				<img
					className="sancor-logo"
					src={SANCOR_LOGO}
					alt="Sancor Seguros"
					width={LOGO_WIDTH}
					height={LOGO_HEIGHT}
				/>
				<Typography className="sancor-tagline">{GANCHO}</Typography>
				<span className="sancor-band-divider" />
				<div className="sancor-band-quote">{policyQuote}</div>
			</div>

			<div className="sancor-panel-quote">{policyQuote}</div>

			<div className="sancor-body">
				<Typography.Title level={4}>Garantía de alquiler</Typography.Title>
				<Typography.Paragraph className="sancor-intro">
					Pedí más información sin compromiso. Completá tus datos y un agente te
					contacta.
				</Typography.Paragraph>

				{status === "success" ? (
					<Typography.Paragraph className="sancor-success">{message}</Typography.Paragraph>
				) : (
					<form className="sancor-form" onSubmit={handleSubmit}>
						<Row gutter={[24, 8]}>
							{CAMPOS.map(campo => (
								<Col xs={24} lg={campo.span} key={campo.name}>
									<label className="sancor-label" htmlFor={`sancor-${campo.name}`}>
										{campo.label}
									</label>
									<input
										className="sancor-input"
										id={`sancor-${campo.name}`}
										name={campo.name}
										type={campo.type}
										autoComplete={campo.autoComplete}
										placeholder={campo.placeholder}
										maxLength={campo.maxLength}
										value={form[campo.name]}
										onChange={handleChange}
										required
									/>
								</Col>
							))}
						</Row>

						{status === "error" && (
							<Typography.Paragraph className="sancor-error">
								{message}
							</Typography.Paragraph>
						)}

						{/*
						 * El `disabled` incluye el loading a propósito: en antd 4.6.6 el
						 * `loading` no deshabilita el botón (sólo le corta los eventos de
						 * puntero), así que un Enter en cualquier campo vuelve a submitear
						 * con el request en vuelo.
						 */}
						<Button
							htmlType="submit"
							className="sancor-submit"
							loading={status === "loading"}
							disabled={isDisabledProperty || status === "loading"}>
							Solicitar mi garantía
						</Button>
					</form>
				)}

				<Typography className="sancor-disclaimer">
					*Valor de referencia, no contempla gastos administrativos.
				</Typography>
			</div>
		</div>
	);
};
